import React from 'react';
import { NewTripRequest, TravelerInfo } from '../../types';

interface StepProps {
  formData: NewTripRequest;
  setFormData: React.Dispatch<React.SetStateAction<NewTripRequest>>;
  onNext?: () => void;
}

const StepDestination: React.FC<StepProps> = ({ formData, setFormData, onNext }) => {
  const handleTravelerChange = (field: keyof TravelerInfo, value: number) => {
    setFormData(prev => ({
      ...prev,
      travelers: {
        ...prev.travelers,
        [field]: value
      }
    }));
  };

  const handleChildAgeChange = (index: number, age: number) => {
    setFormData(prev => {
      const newAges = [...prev.travelers.childAges];
      newAges[index] = age;
      return {
        ...prev,
        travelers: {
          ...prev.travelers,
          childAges: newAges
        }
      };
    });
  };

  const incrementAdults = () => handleTravelerChange('adults', Math.min(20, (formData.travelers.adults || 1) + 1));
  const decrementAdults = () => handleTravelerChange('adults', Math.max(1, (formData.travelers.adults || 1) - 1));

  const incrementChildren = () => {
    const nextChildren = Math.min(10, (formData.travelers.children || 0) + 1);
    const nextAges = Array(nextChildren).fill(0).map((_, i) => formData.travelers.childAges?.[i] ?? 0);
    setFormData(prev => ({ ...prev, travelers: { ...prev.travelers, children: nextChildren, childAges: nextAges } }));
  };

  const decrementChildren = () => {
    const nextChildren = Math.max(0, (formData.travelers.children || 0) - 1);
    const nextAges = (formData.travelers.childAges || []).slice(0, nextChildren);
    setFormData(prev => ({ ...prev, travelers: { ...prev.travelers, children: nextChildren, childAges: nextAges } }));
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Destination Input */}
      <div className="space-y-3">
        <label htmlFor="destination" className="block text-lg font-medium text-gray-900">
          Where do you want to go?
        </label>
        <div className="relative">
          <input
            type="text"
            id="destination"
            name="destination"
            value={formData.destination}
            onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && formData.destination.trim()) {
                onNext && onNext();
              }
            }}
            className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-lg py-4 px-4 bg-gray-50 hover:bg-white transition-colors border"
            placeholder="e.g. Paris, France"
            autoFocus
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
            <span className="text-2xl">🌍</span>
          </div>
        </div>
        <p className="text-sm text-gray-500">
          Enter a city, country, or specific region you'd like to explore.
        </p>
      </div>

      {/* Travelers Section */}
      <div className="space-y-4 pt-4 border-t border-gray-100">
        <h3 className="text-lg font-medium text-gray-900">Who is traveling?</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Adults Counter */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">Adults</span>
              <span className="text-sm text-gray-500">Age 13+</span>
            </div>
            <div className="flex items-center justify-between bg-white rounded-lg p-1 border border-gray-200">
              <button 
                onClick={decrementAdults}
                className="w-10 h-10 flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                disabled={formData.travelers.adults <= 1}
              >
                −
              </button>
              <span className="text-xl font-semibold w-8 text-center">{formData.travelers.adults}</span>
              <button 
                onClick={incrementAdults}
                className="w-10 h-10 flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                disabled={formData.travelers.adults >= 20}
              >
                +
              </button>
            </div>
          </div>

          {/* Children Counter */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">Children</span>
              <span className="text-sm text-gray-500">Ages 0-12</span>
            </div>
            <div className="flex items-center justify-between bg-white rounded-lg p-1 border border-gray-200">
              <button 
                onClick={decrementChildren}
                className="w-10 h-10 flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                disabled={formData.travelers.children <= 0}
              >
                −
              </button>
              <span className="text-xl font-semibold w-8 text-center">{formData.travelers.children}</span>
              <button 
                onClick={incrementChildren}
                className="w-10 h-10 flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                disabled={formData.travelers.children >= 10}
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* Child Ages */}
        {formData.travelers.children > 0 && (
          <div className="mt-4 animate-in fade-in slide-in-from-top-2 duration-300">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Children's Ages
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {Array.from({ length: formData.travelers.children }, (_, i) => (
                <div key={i} className="relative">
                  <input
                    type="number"
                    value={formData.travelers.childAges[i] || ''}
                    onChange={(e) => handleChildAgeChange(i, Math.min(17, Math.max(0, parseInt(e.target.value) || 0)))}
                    min="0"
                    max="17"
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-center py-2 bg-white"
                    placeholder="Age"
                  />
                  <span className="absolute -top-2 -right-2 bg-primary-100 text-primary-800 text-xs font-bold px-1.5 py-0.5 rounded-full border border-white shadow-sm">
                    #{i + 1}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepDestination;

